/**
 * 主大屏运营飘屏：优先级队列 + 去重节流 + 文案模板
 *
 * 优先级越高越先出；同类短时去重，避免轮询把同一状态刷爆。
 */

export const OPS_PRIORITY = {
  need1: 100, // 差1人（最高）
  gather_start: 95, // 开局/进行中
  service: 90, // 服务呼叫
  signal: 88, // 新求组
  gather_new: 85, // 新组局招募
  checkin: 80, // 进场
  notice: 76, // 场管公告
  score: 70, // 打分上榜
  chat: 50, // 短聊天
  gift: 20 // 礼物（降权）
}

const TEMPLATES = {
  need1: ({ play_type }) => `${play_type || '组局'} 差1人 · 速来`,
  signal: ({ play_type, hint, text }) =>
    text || `${play_type || '玩法'} ${hint || '有人求组'}`,
  gather_start: ({ name, user_count, max_user_count }) => {
    const n = name || '组局'
    const seat =
      max_user_count > 0 ? `${user_count || 0}/${max_user_count}` : `${user_count || 0}人`
    return `「${n}」开局 · ${seat}`
  },
  gather_new: ({ name, user_count, max_user_count }) => {
    const n = name || '组局'
    const left =
      max_user_count > 0
        ? `差${Math.max(0, max_user_count - (user_count || 0))}人`
        : `${user_count || 0}人`
    return `新组局「${n}」· ${left}`
  },
  checkin: ({ delta }) => (delta > 1 ? `又有 ${delta} 人到店` : '有人到店了'),
  chat: ({ nickname, text }) => {
    const name = nickname || '用户'
    const t = String(text || '').trim()
    if (!t) return ''
    const short = t.length > 18 ? `${t.slice(0, 17)}…` : t
    return `${name}：${short}`
  },
  score: ({ nickname, play_type, delta }) => {
    const name = nickname || '玩家'
    const pt = play_type || '玩法'
    const d = Number(delta) || 0
    const sign = d > 0 ? `+${d}` : `${d}`
    return `${name} · ${pt} ${sign}`
  },
  notice: ({ text }) => {
    const t = String(text || '').trim()
    if (!t) return ''
    return t.length > 24 ? `公告：${t.slice(0, 23)}…` : `公告：${t}`
  },
  service: (p) => {
    const item = (p && p.item_name) || '服务'
    const tableNote = (p && p.table_note) || ''
    const table = tableNote ? ` · ${tableNote}` : ''
    return `呼叫「${item}」${table}`
  },
  gift: ({ text, nickname }) => text || `${nickname || '用户'} 送出礼物`
}

function normalizeKey(type, key) {
  return `${type}:${key || ''}`
}

/**
 * @param {{ insert: Function, minIntervalMs?: number, dedupeTtlMs?: number }} opts
 */
export function createOpsBannerBus(opts) {
  const insert = opts.insert
  const minIntervalMs = opts.minIntervalMs || 2800
  const dedupeTtlMs = opts.dedupeTtlMs || 45000
  const queue = []
  const seen = new Map() // key -> expireAt
  let timer = null
  let lastEmitAt = 0
  let paused = false

  function pruneSeen(now) {
    seen.forEach((exp, k) => {
      if (exp <= now) seen.delete(k)
    })
  }

  function schedule() {
    if (timer || paused) return
    const wait = Math.max(0, minIntervalMs - (Date.now() - lastEmitAt))
    timer = setTimeout(flush, wait)
  }

  function flush() {
    timer = null
    if (paused) return
    const now = Date.now()
    pruneSeen(now)
    if (!queue.length) return
    queue.sort((a, b) => b.priority - a.priority)
    const next = queue.shift()
    if (!next) return
    try {
      insert(next)
    } catch (e) {
      // ignore insert errors
    }
    lastEmitAt = Date.now()
    if (queue.length) schedule()
  }

  /**
   * @param {{ type: string, text?: string, key?: string, priority?: number, payload?: object, avatar_url?: string, nickname?: string }} evt
   */
  function push(evt) {
    if (!evt || !evt.type) return false
    const type = evt.type
    const tpl = TEMPLATES[type]
    const payload = evt.payload || {}
    const text = (evt.text != null ? String(evt.text) : tpl ? tpl(payload) : '').trim()
    if (!text) return false

    const priority =
      evt.priority != null ? Number(evt.priority) : OPS_PRIORITY[type] != null ? OPS_PRIORITY[type] : 40
    const key = normalizeKey(type, evt.key || text)
    const now = Date.now()
    pruneSeen(now)
    if (seen.has(key)) return false
    seen.set(key, now + dedupeTtlMs)

    // 同 key 已在队列则跳过
    if (queue.some((q) => q._key === key)) return false

    queue.push({
      _key: key,
      type: 'ops',
      opsType: type,
      priority,
      text,
      avatar_url: evt.avatar_url || '',
      nickname: evt.nickname || '',
      background: evt.background || ''
    })
    // 队列过长时丢掉低优先级
    if (queue.length > 24) {
      queue.sort((a, b) => b.priority - a.priority)
      queue.length = 24
    }
    schedule()
    return true
  }

  function hasUrgent(minPriority = 85) {
    return queue.some((q) => q.priority >= minPriority)
  }

  function pause() {
    paused = true
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  function resume() {
    paused = false
    if (queue.length) schedule()
  }

  function clear() {
    queue.length = 0
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  return { push, hasUrgent, pause, resume, clear, OPS_PRIORITY }
}

/**
 * 对比两次 boardSignals，产出运营飘屏事件（首屏 skipEmit=true 只建快照）
 */
export function diffMatchBoard(prev, next) {
  const events = []
  if (!next) return events
  const p = prev || null

  // 求组 / 差1人
  const prevItems = {}
  ;((p && p.items) || []).forEach((it) => {
    if (it && it.play_type) prevItems[it.play_type] = it
  })
  ;((next && next.items) || []).forEach((it) => {
    if (!it || !it.play_type) return
    const before = prevItems[it.play_type]
    const hint = String(it.hint || '')
    const isNeed1 = hint.indexOf('差1') >= 0 || Number(it.count) === 1
    if (!before) {
      events.push({
        type: isNeed1 ? 'need1' : 'signal',
        key: `sig:${it.play_type}:${hint}`,
        payload: it
      })
      return
    }
    const beforeCount = Number(before.count) || 0
    const afterCount = Number(it.count) || 0
    if (isNeed1 && (String(before.hint || '').indexOf('差1') < 0 || beforeCount !== 1)) {
      events.push({
        type: 'need1',
        key: `need1:${it.play_type}`,
        payload: it
      })
    } else if (afterCount > beforeCount) {
      events.push({
        type: 'signal',
        key: `sig:${it.play_type}:${afterCount}`,
        payload: it
      })
    }
  })

  // 组局：新建招募 / 变为进行中
  const prevGathers = {}
  ;((p && p.gathers) || []).forEach((g) => {
    if (g && g.id) prevGathers[g.id] = g
  })
  ;((next && next.gathers) || []).forEach((g) => {
    if (!g || !g.id) return
    const before = prevGathers[g.id]
    const status = Number(g.status) || 0
    if (!before) {
      if (status === 3) {
        events.push({ type: 'gather_start', key: `gs:${g.id}`, payload: g })
      } else {
        events.push({ type: 'gather_new', key: `gn:${g.id}`, payload: g })
      }
      return
    }
    if (Number(before.status) !== 3 && status === 3) {
      events.push({ type: 'gather_start', key: `gs:${g.id}`, payload: g })
    }
  })

  // 进场人数上升
  const prevPresent = p ? Number(p.present_count) || 0 : null
  const nextPresent = Number(next.present_count) || 0
  if (prevPresent != null && nextPresent > prevPresent) {
    events.push({
      type: 'checkin',
      key: `in:${nextPresent}`,
      payload: { delta: nextPresent - prevPresent }
    })
  }

  // 打分流水（ops_feed.scores）
  const prevScoreIds = {}
  ;(((p && p.ops_feed) || {}).scores || []).forEach((s) => {
    if (s && s.id) prevScoreIds[s.id] = true
  })
  ;(((next && next.ops_feed) || {}).scores || []).forEach((s) => {
    if (!s || !s.id || prevScoreIds[s.id]) return
    events.push({
      type: 'score',
      key: `sc:${s.id}`,
      payload: s,
      avatar_url: s.avatar_url,
      nickname: s.nickname
    })
  })

  // 服务呼叫（ops_feed.services）
  const prevSvcIds = {}
  ;(((p && p.ops_feed) || {}).services || []).forEach((s) => {
    if (s && s.id) prevSvcIds[s.id] = true
  })
  ;(((next && next.ops_feed) || {}).services || []).forEach((s) => {
    if (!s || !s.id || prevSvcIds[s.id]) return
    events.push({
      type: 'service',
      key: `sv:${s.id}`,
      payload: s
    })
  })

  // 公告变更
  const prevNotice = String((((p && p.ops_feed) || {}).notice) || '').trim()
  const nextNotice = String((((next && next.ops_feed) || {}).notice) || '').trim()
  if (nextNotice && nextNotice !== prevNotice) {
    events.push({
      type: 'notice',
      key: `nt:${nextNotice.slice(0, 32)}`,
      payload: { text: nextNotice }
    })
  }

  return events
}
